import styled from '@emotion/styled';
import { Typography } from '@material-ui/core';
import { Spacer } from '../../../../../../components/Spacer';
import { useUnsafeTranslation } from '../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../layout/Flex';
import { WithBreakpoints } from '../Breakpoints';
import { NAV_BG_COLOR } from '../Nav/common';
import { TOP_NAV_HEIGHT } from '../TopNav';

const MAX_WIDTHS = {
  left: 384,
  right: 344,
  center: 900
};

const MAX_WIDTH_ALL =
  MAX_WIDTHS.left + MAX_WIDTHS.center + MAX_WIDTHS.right + 8 * 3 * 2;

const MAX_WIDTH_NO_RIGHT = MAX_WIDTHS.left + MAX_WIDTHS.center + 8 * 3 * 1;

export const PageContainer = styled('div')((p) => ({
  backgroundColor: p.theme.custom.colors.gray.w100
}));

export const PageContainerInner = styled('div')((p) => ({
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
}));

export const PAGE_PADDING = 4;

const Container = styled('div')((p) => ({
  position: 'relative',
  paddingLeft: p.theme.spacing(PAGE_PADDING),
  paddingRight: p.theme.spacing(PAGE_PADDING),
  maxWidth: '100%',
  width: '100%'
}));

const ContainerForImageAndHeader = styled('div')((p) => ({
  position: 'sticky',
  top: 56, // nav height
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1,
  background: p.theme.custom.colors.gray.w100
}));

const BANNER_IMAGE_FULL_HEIGHT = 200;
const BANNER_IMAGE_MEDIUM_HEIGHT = 150;
const NO_BANNER_HEADER_HEIGHT = 142;

const BannerImage = styled('img')<{ height: number }>((p) => ({
  marginLeft: -p.theme.spacing(PAGE_PADDING),
  marginRight: -p.theme.spacing(PAGE_PADDING),
  objectFit: 'cover',
  objectPosition: 'top',
  height: p.height
}));

const HeaderContainerWithImage = styled('div')((p) => ({
  position: 'absolute',
  left: 0,
  bottom: 0,
  // marginLeft: p.theme.spacing(5),
  // marginRight: p.theme.spacing(5),
  display: 'flex',
  justifyContent: 'center',
  width: '100%'
}));

const HeaderContainerWithoutImage = styled('div')((p) => ({
  // marginLeft: p.theme.spacing(5),
  // marginRight: p.theme.spacing(5),
  display: 'flex',
  justifyContent: 'center',
  width: '100%'
}));

const HeaderContainerInner = styled('div')<{
  bottomRadius?: boolean;
  noRightColumn?: boolean;
}>((p) => ({
  width: '100%',
  maxWidth: p.noRightColumn ? MAX_WIDTH_NO_RIGHT : MAX_WIDTH_ALL,
  padding: p.theme.spacing(3),
  borderTopLeftRadius: p.theme.custom.border.radius(3),
  borderTopRightRadius: p.theme.custom.border.radius(3),
  borderBottomLeftRadius: p.bottomRadius
    ? p.theme.custom.border.radius(3)
    : undefined,
  borderBottomRightRadius: p.bottomRadius
    ? p.theme.custom.border.radius(3)
    : undefined,
  backgroundColor: 'white'
}));

export const PageFrameHeader: React.FC = ({ children }) => {
  return (
    <WithBreakpoints
      lgMdSm={
        <FlexContainer justifyContent="space-between">{children}</FlexContainer>
      }
      xs={<FlexContainerVertical spacing={2}>{children}</FlexContainerVertical>}
    />
  );
};

const MainContainerLarge = styled('div')((p) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center'
}));

const MainContainerSmall = styled('div')((p) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: p.theme.spacing(5),
  padding: `${p.theme.spacing(4)}px 0`
}));

const MainGrid = styled('div')<{
  noRightColumn: boolean;
  bannerHeight: number;
}>((p) => ({
  display: 'grid',
  gridTemplateColumns: p.noRightColumn
    ? `${MAX_WIDTHS.left}px 1fr`
    : `${MAX_WIDTHS.left}px 1fr ${MAX_WIDTHS.right}px`,
  gridColumnGap: p.theme.spacing(3),
  maxWidth: p.noRightColumn ? MAX_WIDTH_NO_RIGHT : MAX_WIDTH_ALL,
  width: '100%',
  height: `calc(100vh - ${TOP_NAV_HEIGHT}px - ${p.bannerHeight}px)`,
  overflow: 'hidden'
}));

const MainGridItem = styled('div')<{ paddingTop?: number }>((p) => ({
  overflowY: 'auto',
  paddingTop: p.theme.spacing(p.paddingTop || 0)
}));

// The left column's nav components paint their own grey background, but
// only as tall as their content. This wrapper paints the same grey across
// the whole scroll area so a footer pinned below the nav still sits on grey.
const LeftGridItem = styled(MainGridItem)({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: NAV_BG_COLOR
});

const LeftGridItemContent = styled('div')({
  flex: '1 0 auto'
});

const AIDisclosureContainer = styled('div')((p) => ({
  marginTop: 'auto',
  padding: `${p.theme.spacing(2)}px ${p.theme.spacing(3)}px ${p.theme.spacing(
    3
  )}px`
}));

const AIDisclosure = () => {
  const { t } = useUnsafeTranslation();
  return (
    <AIDisclosureContainer>
      <Typography variant="caption">
        {t('Content may have been co-created with AI')}
      </Typography>
    </AIDisclosureContainer>
  );
};

export const PageFrame = ({
  banner,
  header,
  left,
  center,
  right,
  aiDisclosure
}: {
  banner?: {
    url: string;
  } | null;
  header: React.ReactNode;
  left: React.ReactNode;
  center: React.ReactNode;
  right: React.ReactNode;
  /** Shows the "co-created with AI" line at the bottom of the left column, or at the bottom of the page on small screens. */
  aiDisclosure?: boolean;
}) => {
  const leftColumn = (
    <LeftGridItem>
      <LeftGridItemContent>{left}</LeftGridItemContent>
      {aiDisclosure && <AIDisclosure />}
    </LeftGridItem>
  );
  return (
    <Container>
      <ContainerForImageAndHeader>
        {banner?.url ? (
          <>
            <WithBreakpoints
              lgMd={
                <BannerImage
                  src={banner.url}
                  alt={'...'}
                  height={BANNER_IMAGE_FULL_HEIGHT}
                />
              }
              smXs={
                <BannerImage
                  src={banner.url}
                  alt={'...'}
                  height={BANNER_IMAGE_MEDIUM_HEIGHT}
                />
              }
            />
            <HeaderContainerWithImage>
              <HeaderContainerInner noRightColumn={!right}>
                {header}
              </HeaderContainerInner>
            </HeaderContainerWithImage>
          </>
        ) : (
          <>
            <Spacer height={4} />
            <HeaderContainerWithoutImage>
              <HeaderContainerInner bottomRadius={false} noRightColumn={!right}>
                {header}
              </HeaderContainerInner>
            </HeaderContainerWithoutImage>
          </>
        )}
      </ContainerForImageAndHeader>

      <WithBreakpoints
        lg={
          <MainContainerLarge>
            <MainGrid
              noRightColumn={!right}
              bannerHeight={
                banner?.url ? BANNER_IMAGE_FULL_HEIGHT : NO_BANNER_HEADER_HEIGHT
              }
            >
              {leftColumn}
              <MainGridItem paddingTop={4}>{center}</MainGridItem>
              {right && <MainGridItem>{right}</MainGridItem>}
            </MainGrid>
          </MainContainerLarge>
        }
        md={
          <FlexContainerVertical fullWidth spacing={4} marginBottom={3}>
            <MainContainerLarge>
              <MainGrid
                noRightColumn={true}
                bannerHeight={
                  banner?.url
                    ? BANNER_IMAGE_FULL_HEIGHT
                    : NO_BANNER_HEADER_HEIGHT
                }
              >
                {leftColumn}
                <MainGridItem paddingTop={4}>{center}</MainGridItem>
              </MainGrid>
            </MainContainerLarge>
            {right}
          </FlexContainerVertical>
        }
        smXs={
          <MainContainerSmall>
            <div>{left}</div>
            <div>{center}</div>
            <div>{right}</div>
            {aiDisclosure && <AIDisclosure />}
          </MainContainerSmall>
        }
      />
    </Container>
  );
};
